import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchMyTours, fetchMyRegisteredTours } from '../../services/tourService';
import { TourDetailModal } from '../Tours/TourDetailModal';
import { isRecurring, isTourPast, nextOccurrence, describeBookings } from '../../utils/tourSchedule';
import './MyRidesPanel.css';

const STATUS_LABEL = { published: 'Published', draft: 'Draft', pending_payment: 'Pending', cancelled: 'Cancelled' };
const STATUS_COLOR = { published: '#16a34a', draft: '#d97706', pending_payment: '#d97706', cancelled: '#6b7280' };

// The date that matters for a card: a rider's chosen date on a recurring tour
// (regTourDate, "YYYY-MM-DD") takes priority over the tour's own startDate. For
// the organizer's own still-running recurring tour (no regTourDate — that's the
// series itself, not one booking), use its next upcoming run day instead of the
// stale date it was first created on, so it sorts and displays sensibly.
function effectiveDate(t) {
  if (t.regTourDate) {
    const [y, m, d] = t.regTourDate.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  if (isRecurring(t)) return nextOccurrence(t) || (t.startDate?.toDate ? t.startDate.toDate() : null);
  return t.startDate?.toDate ? t.startDate.toDate() : (t.startDate ? new Date(t.startDate) : null);
}
function startMs(t) {
  const d = effectiveDate(t);
  return d ? d.getTime() : Infinity;
}
// Whether this card belongs in "Past": a rider's own past occurrence of a
// recurring tour they attended, or a genuinely-ended tour (isTourPast already
// treats an active recurring tour as upcoming for as long as it still runs).
function isPastCard(t) {
  if (t.regTourDate) return startMs(t) < Date.now() - 86_400_000;
  return isTourPast(t);
}
function fmtDate(t) {
  const d = effectiveDate(t);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD';
}

export function MyRidesPanel({ onClose }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('riding');           // 'riding' | 'organizing'
  const [riding, setRiding] = useState(null);
  const [organizing, setOrganizing] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchMyRegisteredTours(user.uid).then(setRiding).catch(err => { console.error('My Rides (riding) load failed:', err); setRiding([]); });
    fetchMyTours(user.uid).then(setOrganizing).catch(err => { console.error('My Rides (organizing) load failed:', err); setOrganizing([]); });
  }, [user]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = tab === 'riding' ? riding : organizing;
  const upcoming = (list || []).filter(t => !isPastCard(t)).sort((a, b) => startMs(a) - startMs(b));
  const past     = (list || []).filter(t =>  isPastCard(t)).sort((a, b) => startMs(b) - startMs(a));

  const Card = ({ t }) => {
    const place = t.meetingPoint?.name || t.startName || '';
    const price = t.registrationType !== 'paid' ? 'Free'
      : t.category === 'lesson' ? `$${Number(t.hourlyRate || 0).toFixed(0)}/hr`
      : `$${Number(t.ticketPrice || 0).toFixed(0)}`;
    const regsLabel = describeBookings(t);
    // Cancelled or already-past tours are shown muted with a grey date-aware badge.
    const isCancelled = t.status === 'cancelled';
    const isPast = isPastCard(t);
    // A rider who rode a past occurrence of a still-running recurring tour has
    // COMPLETED it — not "past". The tour itself carries on, so no dimming.
    const isCompletedOccurrence =
      tab === 'riding' && isPast && !isCancelled && !!t.regTourDate && isRecurring(t);
    const muted = (isCancelled || isPast) && !isCompletedOccurrence;
    const badge = isCancelled
      ? { label: 'Cancelled', color: '#6b7280' }
      : isCompletedOccurrence
        ? { label: 'Completed', color: '#16a34a' }
        : isPast
          ? { label: 'Past', color: '#6b7280' }
          : tab === 'organizing'
            ? { label: STATUS_LABEL[t.status] ?? t.status, color: STATUS_COLOR[t.status] ?? '#6b7280' }
            : { label: t.registrationType === 'paid' ? 'Ticket' : 'Registered', color: '#16a34a' };
    return (
      <li className={`mr-card${muted ? ' mr-card--muted' : ''}`} onClick={() => setSelected(t)}>
        {t.imageUrls?.[0]
          ? <img className="mr-card__thumb" src={t.imageUrls[0]} alt="" loading="lazy" />
          : <div className="mr-card__thumb mr-card__thumb--ph">{t.name?.[0]?.toUpperCase()}</div>}
        <div className="mr-card__info">
          <div className="mr-card__top">
            <span className="mr-card__name">{t.name}</span>
            <span className="mr-card__badge" style={{ color: badge.color, background: badge.color + '18' }}>{badge.label}</span>
          </div>
          <span className="mr-card__date">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {fmtDate(t)}
          </span>
          <div className="mr-card__meta">
            {place && (
              <span className="mr-card__meta-item">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                {place}
              </span>
            )}
            <span className="mr-card__meta-item">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              {price}
            </span>
            {tab === 'organizing' && (
              <span className="mr-card__meta-item">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
                {regsLabel}
              </span>
            )}
          </div>
        </div>
      </li>
    );
  };

  const Section = ({ title, items }) => items.length > 0 && (
    <div className="mr-section">
      <div className="mr-section__title">{title} <span className="mr-section__count">{items.length}</span></div>
      <ul className="mr-list">{items.map(t => <Card key={t.id} t={t} />)}</ul>
    </div>
  );

  const loading = list === null;
  const empty = !loading && upcoming.length === 0 && past.length === 0;

  const panel = createPortal(
    <div className="mr-overlay" onClick={e => e.target.classList.contains('mr-overlay') && onClose()}>
      <aside className="mr-panel">
        <div className="mr-header">
          <h2 className="mr-title">My Rides</h2>
          <button className="mr-close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/></svg>
          </button>
        </div>

        <div className="mr-tabs">
          <button className={`mr-tab${tab === 'riding' ? ' mr-tab--active' : ''}`} onClick={() => setTab('riding')}>Riding</button>
          <button className={`mr-tab${tab === 'organizing' ? ' mr-tab--active' : ''}`} onClick={() => setTab('organizing')}>Organizing</button>
        </div>

        <div className="mr-body">
          {loading && <div className="mr-loading"><span className="mr-spinner" /> Loading…</div>}
          {empty && (
            <div className="mr-empty">
              <div className="mr-empty__icon">🏍</div>
              <div className="mr-empty__title">{tab === 'riding' ? 'No rides yet' : 'No tours yet'}</div>
              <div className="mr-empty__sub">
                {tab === 'riding' ? 'Tours you register for will show up here.' : 'Tours you publish will show up here.'}
              </div>
            </div>
          )}
          {!loading && !empty && (
            <>
              <Section title="Upcoming" items={upcoming} />
              <Section title="Past" items={past} />
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );

  return (
    <>
      {panel}
      {selected && (
        <TourDetailModal
          tour={selected}
          onClose={() => setSelected(null)}
          onCancelled={() => { setSelected(null); if (user) { fetchMyTours(user.uid).then(setOrganizing).catch(() => {}); fetchMyRegisteredTours(user.uid).then(setRiding).catch(() => {}); } }}
        />
      )}
    </>
  );
}
